import { InspectionArea, ProductType, Project, SubscriptionType, UserInfo } from '@infraloop/services'
import dayjs from 'dayjs'

/** Dashboard tabs: projects the user created vs. projects shared with them. */
export enum ProjectTab {
  Owned = 'own',
  Invited = 'shared',
}

/** Direction a sortable column is ordered in. */
export enum SortDirection {
  Asc = 'asc',
  Desc = 'desc',
}

/** Sortable columns shared by the project table and the area sub-table. */
export enum SortKey {
  Name = 'name',
  Size = 'size',
  Updated = 'updated',
  Created = 'created',
}

/** Which column is sorted and in which direction. */
export interface SortState {
  key: SortKey
  direction: SortDirection
}

/** Default sort applied to both tables — newest updates first (matches the mockup). */
export const DEFAULT_SORT: SortState = { key: SortKey.Updated, direction: SortDirection.Desc }

/**
 * Compute the next sort state when a column header is clicked: toggle direction if the
 * column is already active, otherwise switch to it ascending.
 * @param current Current sort state.
 * @param key Clicked column.
 */
export const nextSort = (current: SortState, key: SortKey): SortState => {
  if (current.key === key) {
    const direction = current.direction === SortDirection.Desc ? SortDirection.Asc : SortDirection.Desc
    return { key, direction }
  }
  return { key, direction: SortDirection.Asc }
}

/** Bytes per unit step used by {@link bytesToHumanReadable}. */
const BYTE_UNIT_STEP = 1024

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

/** Smallest unit shown by {@link bytesToHumanReadable} — sub-MB sizes round up to MB. */
const MIN_UNIT_INDEX = BYTE_UNITS.indexOf('MB')

/**
 * Format a byte count into a human-readable string (e.g. `1.94 GB`), never using a unit
 * smaller than MB.
 * @param bytes Number of bytes.
 */
export const bytesToHumanReadable = (bytes: number): string => {
  if (!bytes || bytes <= 0) return `0 ${BYTE_UNITS[MIN_UNIT_INDEX]}`
  const rawIndex = Math.floor(Math.log(bytes) / Math.log(BYTE_UNIT_STEP))
  const i = Math.min(Math.max(rawIndex, MIN_UNIT_INDEX), BYTE_UNITS.length - 1)
  const value = parseFloat((bytes / BYTE_UNIT_STEP ** i).toFixed(1))
  return `${value} ${BYTE_UNITS[i]}`
}

/**
 * Format an epoch-milliseconds timestamp as `YYYY/MM/DD`, matching the mockup.
 * @param epochMs Timestamp in milliseconds.
 */
export const formatDate = (epochMs?: number): string => {
  if (!epochMs) return '—'
  return dayjs(epochMs).format('YYYY/MM/DD')
}

/** Total stored size of a project in bytes. */
export const getProjectSize = (project: Project): number => project.stats?.total_size ?? 0

/** Stored size of an inspection area in bytes, preferring the original upload. */
export const getAreaSize = (area: InspectionArea): number => area.origin_file?.size ?? area.down_sampled_file?.size ?? 0

/** Number of viewers the project is shared with. */
export const getProjectViewerCount = (project: Project): number => Object.keys(project.shared_user ?? {}).length

/**
 * Derive up-to-two-letter initials for the user avatar from a name or email.
 * @param value Display name or email address.
 */
export const getInitials = (value?: string): string => {
  if (!value) return '?'
  const localPart = value.split('@')[0]
  const segments = localPart.split(/[.\-_+\s]+/).filter(Boolean)
  if (segments.length === 0) return localPart.slice(0, 2).toUpperCase()
  if (segments.length === 1) return segments[0].slice(0, 2).toUpperCase()
  return `${segments[0][0]}${segments[1][0]}`.toUpperCase()
}

/** Pragmatic email shape check — a local part, an @, and a dotted domain. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Whether the given string is a plausible email address.
 * @param value Candidate email string.
 */
export const isValidEmail = (value: string): boolean => EMAIL_PATTERN.test(value.trim())

/**
 * Build the in-app route that opens an inspection area in the editor. The path shape is
 * frozen (it's baked into customer-distributed links); the route itself is mounted by
 * slice #127.
 * @param projectId Project the area belongs to.
 * @param inspectionAreaId Inspection area to open.
 */
export const buildEditorUrl = (projectId: string, inspectionAreaId: string): string =>
  `/projects/${projectId}/editor?area=${inspectionAreaId}`

/**
 * Build the in-app route to a project's report (帳票) page — the same inspection-sheet
 * route the legacy dashboard navigated to. Passing an area focuses its sheet, which is
 * where the report export lives.
 * @param projectId Project whose report to open.
 * @param inspectionAreaId Optional inspection area to focus.
 */
export const buildReportUrl = (projectId: string, inspectionAreaId?: string): string => {
  const base = `/projects/${projectId}/inspection-sheet`
  return inspectionAreaId ? `${base}?area=${inspectionAreaId}` : base
}

/**
 * Build the in-app route to an area's PCD alignment (位置合わせ) page — the same destination
 * as the editor header's 位置合わせ button.
 * @param projectId Project the area belongs to.
 * @param inspectionAreaId Inspection area to align.
 */
export const buildAlignmentUrl = (projectId: string, inspectionAreaId: string): string =>
  `/projects/${projectId}/pcd-alignment?area=${inspectionAreaId}`

/** In-app route to the plan section of the user page — the upgrade destination. */
export const buildPlanUrl = (): string => `/user#plan`

/** A project (and optional area) the dashboard should reveal on load, from the URL hash. */
export interface DashboardHashTarget {
  /** Project to expand and reveal. */
  projectId?: string
  /** Area to scroll to and highlight within that project. */
  areaId?: string
}

/**
 * Build the dashboard route that reveals a project (and optionally an area) on arrival —
 * used by the editor's back button. The ids ride in the URL hash so the dashboard can select
 * the project's tab, auto-expand it, and scroll to / highlight the area: `#<projectId>` or
 * `#<projectId>#<areaId>`.
 * @param projectId Project to reveal.
 * @param inspectionAreaId Optional area to scroll to and highlight.
 */
export const buildDashboardUrl = (projectId: string, inspectionAreaId?: string): string =>
  inspectionAreaId ? `/#${projectId}#${inspectionAreaId}` : `/#${projectId}`

/**
 * Parse the dashboard's reveal hash (see {@link buildDashboardUrl}) into its project and area
 * ids. Accepts the hash with or without its leading `#`.
 * @param hash The URL hash (e.g. `#<projectId>#<areaId>`).
 */
export const parseDashboardHash = (hash: string): DashboardHashTarget => {
  const [projectId, areaId] = hash.replace(/^#/, '').split('#')
  return { projectId: projectId || undefined, areaId: areaId || undefined }
}

/**
 * Whether the user may create projects. Mirrors the legacy dashboard's rule: only paying
 * users — those owning the Hatsuly or Markly product — may create.
 * @param user Signed-in user's profile (undefined while loading).
 */
export const canCreateProject = (user?: UserInfo): boolean =>
  Boolean(user?.owned_products?.includes(ProductType.Hatsuly) || user?.owned_products?.includes(ProductType.Markly))

/**
 * Whether the user is on one of the deprecated subscription types, which predate the
 * self-managed-subscription (and terms-of-service) flow.
 * @param user Signed-in user's profile (undefined while loading).
 */
export const hasDeprecatedSubscription = (user?: UserInfo): boolean =>
  user?.subscriptions?.type === SubscriptionType.Base || user?.subscriptions?.type === SubscriptionType.Features

/**
 * Compare two primitive values honouring the sort direction.
 * @param a First value.
 * @param b Second value.
 * @param direction Sort direction.
 */
const compareValues = (a: string | number, b: string | number, direction: SortDirection): number => {
  let result = 0
  if (a < b) result = -1
  else if (a > b) result = 1
  return direction === SortDirection.Desc ? -result : result
}

/** Extract the comparable value for a project column. */
const getProjectSortValue = (project: Project, key: SortKey): string | number => {
  switch (key) {
    case SortKey.Name:
      return (project.project_name ?? '').toLowerCase()
    case SortKey.Size:
      return getProjectSize(project)
    case SortKey.Updated:
      return project.updated_at ?? 0
    case SortKey.Created:
      return project.created_at ?? 0
    default:
      return 0
  }
}

/** Extract the comparable value for an inspection-area column. */
const getAreaSortValue = (area: InspectionArea, key: SortKey): string | number => {
  switch (key) {
    case SortKey.Name:
      return (area.inspection_area_name ?? '').toLowerCase()
    case SortKey.Size:
      return getAreaSize(area)
    case SortKey.Updated:
      return area.updated_at ?? 0
    case SortKey.Created:
      return area.created_at ?? 0
    default:
      return 0
  }
}

/**
 * Filter projects by name and sort them by the active column.
 * @param projects Projects to process.
 * @param search Case-insensitive name filter.
 * @param sort Active sort column and direction.
 */
export const filterAndSortProjects = (projects: Project[], search: string, sort: SortState): Project[] => {
  const query = search.trim().toLowerCase()
  const filtered = query ? projects.filter((p) => (p.project_name ?? '').toLowerCase().includes(query)) : projects
  return [...filtered].sort((a, b) =>
    compareValues(getProjectSortValue(a, sort.key), getProjectSortValue(b, sort.key), sort.direction),
  )
}

/**
 * Filter inspection areas by name and sort them by the active column.
 * @param areas Areas to process.
 * @param search Case-insensitive name filter.
 * @param sort Active sort column and direction.
 */
export const filterAndSortAreas = (areas: InspectionArea[], search: string, sort: SortState): InspectionArea[] => {
  const query = search.trim().toLowerCase()
  const filtered = query ? areas.filter((a) => (a.inspection_area_name ?? '').toLowerCase().includes(query)) : areas
  return [...filtered].sort((a, b) =>
    compareValues(getAreaSortValue(a, sort.key), getAreaSortValue(b, sort.key), sort.direction),
  )
}
